"use client";
import type { GameData } from "@/app/games/(details)/types";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGalleryAnalytics } from "@/context/AnalyticsProvider/gallery";
import { cn } from "@/lib/utils";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useEffect, useState } from "react";
import { GamePageModal } from "../GamePageModal";

const FullScreenMedia = dynamic(
  () =>
    import("../FullScreenMedia/FullScreenMedia").then((m) => m.FullScreenMedia),
  {
    ssr: false,
  },
);

interface ArtGalleryModalProps {
  gameData: GameData;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
}

export const ArtGalleryModal = ({
  gameData,
  isModalOpen,
  setIsModalOpen,
}: ArtGalleryModalProps) => {
  const { track } = useGalleryAnalytics();
  const [isImgOpen, setIsImgOpen] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  useEffect(() => {
    if (isModalOpen && gameData.details.game_id) {
      track({
        action: Action.VIEWED,
        extras: {
          id: "art-gallery-modal",
          gameId: gameData.details.game_id,
          gameName: gameData.details.name,
          version: "v3",
        },
      });
    }
  }, [isModalOpen, gameData.details.game_id, gameData.details.name, track]);

  const handleImageOpen = (index: number) => {
    if (gameData.details.game_id) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "art-gallery-image",
          imageIndex: index.toString(),
          control: "ArtGalleryImage",
          controlType: ControlType.IMAGE,
          gameId: gameData.details.game_id,
          gameName: gameData.details.name,
          version: "v3",
        },
      });
    }
    setImgIndex(index);
    setIsImgOpen(true);
  };

  const handleImageHover = (index: number) => {
    if (gameData.details.game_id) {
      track({
        action: Action.HOVER,
        extras: {
          id: "art-gallery-image-hover",
          imageIndex: index.toString(),
          control: "Gallery",
          controlType: ControlType.IMAGE,
          gameId: gameData.details.game_id,
          gameName: gameData.details.name,
          version: "v3",
        },
      });
    }
    setImgIndex(index);
  };

  const handleCloseImg = () => {
    if (gameData.details.game_id) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "art-gallery-close-fullscreen",
          control: "ModalClose",
          controlType: ControlType.BUTTON,
          gameId: gameData.details.game_id,
          gameName: gameData.details.name,
          version: "v3",
        },
      });
    }
    setIsImgOpen(false);
  };

  return (
    <>
      <GamePageModal
        title="Art Gallery"
        gameTitle={gameData.details.name}
        gameIcon={gameData.details.logo_url}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        className="h-[95%] top-[52.5%] before:rounded-b-none [&>div]:rounded-b-none"
        gameId={gameData.details.game_id}
      >
        {/* Bento/Masonry Images Grid */}
        <div className="z-50 relative p-5 grid gap-5 overflow-y-auto auto-rows-[max-content] grid-cols-1 md:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(min(100%,380px),1fr))] md:auto-rows-[minmax(200px,240px)] lg:auto-rows-[minmax(220px,280px)] xl:auto-rows-[minmax(200px,320px)]">
          {gameData.gameContent?.art_gallery_urls?.map((imageUrl, index) => (
            <button
              type="button"
              key={imageUrl}
              className={cn(
                "relative overflow-hidden rounded-lg group cursor-pointer",
                index % 2 === 0 ? "md:row-span-2" : "md:row-span-1",
              )}
              onClick={() => handleImageOpen(index)}
              onMouseEnter={() => handleImageHover(index)}
              aria-label={`View ${gameData.details.name ?? "game"} art ${index + 1}`}
            >
              <Image
                src={imageUrl}
                alt={`${gameData.details.name ?? "Game"} Art ${index + 1}`}
                fill
                className="object-cover !relative transition-transform duration-300 ease-in-out group-hover:scale-105"
                sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
              />
            </button>
          ))}
        </div>
      </GamePageModal>

      <FullScreenMedia
        mediaURL={gameData.gameContent?.art_gallery_urls?.[imgIndex] ?? ""}
        gameTitle={gameData.details.name}
        handleCloseMedia={handleCloseImg}
        isMediaOpen={isImgOpen}
        track={
          track as unknown as (event: {
            action: Action;
            extras: Record<string, unknown>;
          }) => void
        }
        gameId={gameData.details.game_id}
      />
    </>
  );
};
